import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { OrderPaymentStatus } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be-features-ordering';
import {
  type FetchPaymentStateParams,
  type FetchPaymentStateResponse,
} from '@sg/shared-fe-features-ordering';

import { checkIfFailureWasAlreadyShown, getLocalStorageOrderId } from '@order/features-ordering';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import {
  BagPaymentStateDocument,
  type BagPaymentStateQuery,
} from '../../GraphQL/BagPaymentState.generated';

export const useFetchOrderPaymentState = () => {
  const client = useClient();
  const isLoggedIn = useIsLoggedIn();

  const fetchOrderPaymentState = useMemo(
    () =>
      fromPromise<FetchPaymentStateResponse, FetchPaymentStateParams>(async ({ input }) => {
        if (!isLoggedIn) throw new Error('Customer is not logged in');

        const orderIdFromInput = input?.orderId;
        const { orderId: orderIdFromStorage, orderType: storedType } = await getLocalStorageOrderId(); // prettier-ignore
        const orderId = orderIdFromInput || orderIdFromStorage;

        const query = client.query<BagPaymentStateQuery>;

        if (!orderId) throw new Error('Order ID is required');

        if (!orderIdFromInput && storedType !== OrderTypeValues.GravyOrder) {
          throw new Error('Must be gravy order');
        }

        const response = await query(
          BagPaymentStateDocument,
          { orderId, orderType },
          { requestPolicy },
        ).toPromise();

        const paymentState = response.data?.orderPaymentState;
        if (!paymentState) throw new Error('Order payment state not found');

        const paymentIntentId = paymentState.paymentIntentId;
        const maybeUpdatedAt = Number(paymentState.updatedAt);

        const isStatusFailed = paymentState.status === OrderPaymentStatus.Failed;
        const isStatusReleased = paymentState.status === OrderPaymentStatus.Released;
        const hasError = Boolean(paymentState.error);

        const isFailure = isStatusFailed || isStatusReleased || hasError;
        const hasAlreadyShownFailure = isFailure
          ? await checkIfFailureWasAlreadyShown(paymentIntentId)
          : false;

        const paymentStateExpiration = Number.isNaN(maybeUpdatedAt) ? undefined : maybeUpdatedAt;
        const paymentStateStatus = hasAlreadyShownFailure
          ? OrderPaymentStatus.Released
          : paymentState.status;
        const paymentStateError = hasAlreadyShownFailure ? undefined : paymentState.error;

        return {
          orderId,
          paymentIntentId,
          paymentStateExpiration,
          paymentStateStatus,
          paymentStateError,
        };
      }),
    [isLoggedIn, client.query],
  );

  return { fetchOrderPaymentState };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const requestPolicy = 'network-only' as const;
const orderType = OrderTypeValues.GravyOrder;
